import React from "react";
import A4PageFrame from "./A4PageFrame";
import { fmtNum, fmtDate } from "./formatters";

// "Données climatiques" — ETC / DJF / NJ imported climate-normalization values.
// Pass #3: the client asked for a Matrice-pondérations-style layout — LANDSCAPE,
// ONE combined table with the period on the left (Début | Fin) and the three
// metric values on the right (ETC | DJF | NJ), one row per month + a Total row.
//
// The backend returns three SEPARATE arrays (etc/djf/nj = [{start_date,end_date,
// value}]) that are NOT guaranteed aligned/equal-length, so we merge by the
// start_date|end_date pair.
//
// Props
//   data           GET /repartition-report/etc-djf-nj → { etc, djf, nj, totals }
//   building, periodLabel, whiteLabelling, logoUrl, basePageNumber, grandTotalPages

const EtcDjfNj = ({
  data,
  building,
  periodLabel,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const etc = data?.etc || [];
  const djf = data?.djf || [];
  const nj  = data?.nj || [];
  const totals = data?.totals || { etc: 0, djf: 0, nj: 0 };

  const rows = mergeByPeriod(etc, djf, nj);
  const hasData = rows.length > 0;

  return (
    <A4PageFrame
      building={building}
      periodLabel={periodLabel}
      sectionTitle="Données climatiques (ETC / DJF / NJ)"
      whiteLabelling={whiteLabelling}
      logoUrl={logoUrl}
      pageNumber={basePageNumber}
      totalPages={grandTotalPages}
      landscape
    >
      {!hasData ? (
        <div style={{ marginTop: 20, fontSize: 10, color: "#4a4c6b", fontStyle: "italic" }}>
          Aucune donnée climatique importée pour cette période.
        </div>
      ) : (
        <table style={tbl}>
          <colgroup>
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 200 }} />
          </colgroup>
          <thead>
            {/* Two-line header: metric code over its full French label, mirroring
                the Matrice pondérations sheet. */}
            <tr style={{ borderBottom: "1px solid #00033F" }}>
              <th style={th}>Début</th>
              <th style={th}>Fin</th>
              <th style={thNum}>ETC</th>
              <th style={thNum}>DJF</th>
              <th style={thNum}>NJ</th>
            </tr>
            <tr style={{ borderBottom: "1px solid #00033F" }}>
              <th style={thSub} />
              <th style={thSub} />
              <th style={thSubNum}>Écarts de température cumulés</th>
              <th style={thSubNum}>Degrés-jours de refroidissement</th>
              <th style={thSubNum}>Nombre de jours</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={td}>{fmtDate(r.start_date)}</td>
                <td style={td}>{fmtDate(r.end_date)}</td>
                <td style={tdNum}>{fmtNum(r.etc)}</td>
                <td style={tdNum}>{fmtNum(r.djf)}</td>
                <td style={tdNum}>{fmtNum(r.nj)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: "1px solid #00033F", fontWeight: 700 }}>
              <td style={td} colSpan={2}>Total</td>
              <td style={tdNum}>{fmtNum(totals.etc)}</td>
              <td style={tdNum}>{fmtNum(totals.djf)}</td>
              <td style={tdNum}>{fmtNum(totals.nj)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </A4PageFrame>
  );
};

// Merge the three per-metric arrays into one row per Début|Fin period. The three
// arrays are queried independently and are not guaranteed aligned, so we key on
// the start/end pair and sort by start_date.
function mergeByPeriod(etc, djf, nj) {
  const map = new Map();
  const key = (r) => `${r.start_date}|${r.end_date}`;
  const ensure = (r) => {
    const k = key(r);
    if (!map.has(k)) map.set(k, { start_date: r.start_date, end_date: r.end_date, etc: null, djf: null, nj: null });
    return map.get(k);
  };
  for (const r of etc) ensure(r).etc = r.value;
  for (const r of djf) ensure(r).djf = r.value;
  for (const r of nj)  ensure(r).nj  = r.value;
  return [...map.values()].sort((a, b) =>
    String(a.start_date || "").localeCompare(String(b.start_date || "")));
}

// Landscape table styles mirroring PonderationMatrice: fixed layout, 25px
// deterministic rows, left text cols + right-aligned numeric cols.
const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4, marginBottom: 0, tableLayout: "fixed" };
const th = { padding: "3px 6px", fontWeight: 600, textAlign: "left", fontSize: 11 };
const thNum = { ...th, textAlign: "right" };
const thSub = { padding: "0 6px 3px 6px", fontWeight: 400, textAlign: "left", fontSize: 8.5, color: "#4a4c6b" };
const thSubNum = { ...thSub, textAlign: "right" };
const td = {
  boxSizing: "border-box",
  height: 25,
  padding: "5px 6px",
  fontSize: 10,
  lineHeight: "14px",
  borderBottom: "1px solid rgba(0,3,63,0.05)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default EtcDjfNj;
